import React, { useEffect, useState } from 'react'
import {Bell, X} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { formatNotification, NEW_REQUEST_NOTIFICATIONS } from '../data/notifications'
import toast from 'react-hot-toast'
import { requestNotificationPermission, showLocalNotification } from '../utils/pwaInstaller'

interface Notification {
  id: string
  title: string
  body: string
  timestamp: Date
  read: boolean
  urgency: 'critical' | 'high' | 'medium'
}

export function NotificationSystem() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [hasPermission, setHasPermission] = useState(false)

  // Solicitar permissão push ao montar
  useEffect(() => {
    requestNotificationPermission().then(granted => setHasPermission(granted))
  }, [])

  useEffect(() => {
    // Simular notificações de novos pedidos em tempo real
    const checkNewRequests = async () => {
      try {
        const { list } = await lumi.entities.requests.list({
          filter: { status: 'active' },
          sort: { createdAt: -1 },
          limit: 5
        })

        // Para cada pedido recente, criar notificação
        const recentRequests = list.filter((req: any) => {
          const createdAt = new Date(req.createdAt)
          const now = new Date()
          const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60)
          return diffMinutes < 60 // Pedidos das últimas 1 hora
        })

        recentRequests.forEach((req: any) => {
          const notificationData = formatNotification(
            req.urgency === 'critical' 
              ? NEW_REQUEST_NOTIFICATIONS.critical 
              : NEW_REQUEST_NOTIFICATIONS.high,
            {
              bloodType: req.bloodType,
              city: req.city,
              distance: '5' // Simular distância
            }
          )

          // Mostrar toast E push notification para críticas
          if (req.urgency === 'critical') {
            toast.error(notificationData.title, {
              duration: 8000,
              icon: '🚨'
            })
            // Push notification real
            if (hasPermission) {
              showLocalNotification(
                notificationData.title,
                notificationData.body,
                '/solicitacoes'
              )
            }
          }

          // Adicionar à lista de notificações
          const newNotification: Notification = {
            id: req._id,
            title: notificationData.title,
            body: notificationData.body,
            timestamp: new Date(req.createdAt),
            read: false,
            urgency: req.urgency
          }

          setNotifications(prev => {
            // Evitar duplicatas
            if (prev.some(n => n.id === newNotification.id)) return prev
            return [newNotification, ...prev].slice(0, 10) // Manter apenas 10
          })
        })
      } catch (error) {
        console.error('Erro ao verificar notificações:', error)
      }
    }

    // Verificar imediatamente
    checkNewRequests()

    // Verificar a cada 3 minutos (performance)
    const interval = setInterval(checkNewRequests, 180000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.read).length)
  }, [notifications])

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    )
  }

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'border-red-500 bg-red-50'
      case 'high': return 'border-orange-500 bg-orange-50'
      case 'medium': return 'border-yellow-500 bg-yellow-50'
      default: return 'border-gray-300 bg-gray-50'
    }
  }

  return (
    <div className="relative">
      {/* Botão de Notificações */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
      >
        <Bell className="h-5 w-5 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Painel de Notificações */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-[600px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <h3 className="font-bold text-gray-900">Notificações</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-red-600 hover:text-red-700 font-medium"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* Lista de Notificações */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Nenhuma notificação no momento</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      !notification.read ? 'bg-blue-50/50' : ''
                    }`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className={`inline-block px-2 py-1 rounded-full text-xs font-semibold mb-2 border ${getUrgencyColor(notification.urgency)}`}>
                          {notification.urgency === 'critical' && '🚨 Crítico'}
                          {notification.urgency === 'high' && '⚠️ Urgente'}
                          {notification.urgency === 'medium' && '📢 Novo'}
                        </div>
                        <h4 className="font-bold text-sm text-gray-900 mb-1">
                          {notification.title}
                        </h4>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {notification.body}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatTimestamp(notification.timestamp)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          removeNotification(notification.id)
                        }}
                        className="ml-2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <X className="h-4 w-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function formatTimestamp(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Agora mesmo'
  if (diffMins < 60) return `Há ${diffMins} minuto${diffMins > 1 ? 's' : ''}`
  if (diffHours < 24) return `Há ${diffHours} hora${diffHours > 1 ? 's' : ''}`
  if (diffDays < 7) return `Há ${diffDays} dia${diffDays > 1 ? 's' : ''}`
  return date.toLocaleDateString('pt-BR')
}
